import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { noop } from 'lodash';

import { useStack } from 'containers/StackProvider';
import { ClickOutside, ClickOutsideProps } from 'components/Utilities';

import { omitProps } from 'utilsTS/react';
import { ToggleableContext } from 'utilsTS/hooks';

import styles from '../Modal.module.scss';

export interface ModalDialogProps extends Omit<ClickOutsideProps, 'onTrigger' | 'children'> {
    ref?: React.Ref<HTMLDivElement>;
    id: string;
    depth?: number;
    small?: boolean;
    teaser?: boolean;
    drawer?: boolean;
    confirmation?: boolean;
    embedded?: boolean;
    closeOnEsc?: boolean;
    closeOnClick?: boolean;
    onOutsideClick?: ClickOutsideProps['onTrigger'];
    children?: React.ReactNode;
}

export const ModalDialog: React.ForwardRefExoticComponent<ModalDialogProps> = React.forwardRef(
    (
        {
            depth = 0,
            small = false,
            teaser = false,
            drawer = false,
            confirmation = false,
            embedded = false,
            closeOnEsc = true,
            closeOnClick = false,
            // eslint-disable-next-line react/prop-types
            onOutsideClick = noop,
            id,
            className,
            children,
            ...rest
        },
        forwardedRef,
    ) => {
        const { isOpen, close } = React.useContext(ToggleableContext);
        const stack = [...useStack(drawer ? 'drawer' : 'modal', id, isOpen)].reverse();

        const getDepthClassName = () => {
            if (!drawer) return null;
            if (depth > 0) return `g-modal-depth-${depth}`;
            return `g-modal-depth-${stack.includes(id) ? stack.indexOf(id) : 0}`;
        };

        const classNames = cx(
            'modal',
            'g-modal',
            !small && !teaser && !confirmation && 'g-modal-regular',
            small && 'g-modal-small',
            teaser && 'g-modal-teaser',
            drawer && 'g-modal-drawer',
            confirmation && 'g-modal-confirmation',
            embedded && 'g-modal-embedded',
            styles.modal,
        );

        const isClickOutside = React.useCallback(
            (event: MouseEvent) => !!(event.target as Element)?.querySelector(`#${id}`),
            [id],
        );

        const handleTrigger = React.useCallback<ClickOutsideProps['onTrigger']>(
            (event, isClick) => {
                onOutsideClick(event, isClick);
                close();
            },
            [onOutsideClick, close],
        );

        return (
            <div ref={forwardedRef} className={classNames}>
                <ClickOutside
                    {...omitProps(rest, ModalDialog)}
                    aria-modal
                    active={isOpen}
                    id={id}
                    role="dialog"
                    aria-labelledby={`${id}-title`}
                    triggerOnEsc={closeOnEsc}
                    triggerOnClick={closeOnClick}
                    className={cx('modal-dialog', styles.dialog, getDepthClassName(), className)}
                    isClickOutside={isClickOutside}
                    onTrigger={handleTrigger}
                >
                    <div className={cx('modal-content', styles.content)}>{children}</div>
                </ClickOutside>
            </div>
        );
    },
);

ModalDialog.displayName = 'ModalDialog';
ModalDialog.propTypes = {
    id: PropTypes.string.isRequired,
    depth: PropTypes.number,
    small: PropTypes.bool,
    teaser: PropTypes.bool,
    drawer: PropTypes.bool,
    confirmation: PropTypes.bool,
    embedded: PropTypes.bool,
    closeOnClick: PropTypes.bool,
    closeOnEsc: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node,
};
